/**
 * Currency Picker Component
 * 
 * Dropdown component for selecting currencies.
 * Following Tabby design principles: minimal, functional, clear.
 */

import React from 'react';
import { getCurrencyOptions, getCurrencySymbol } from '../utils/currency';

interface CurrencyPickerProps {
  value: string;
  onChange: (currencyCode: string) => void;
  label?: string;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  showSymbol?: boolean;
}

const CurrencyPicker: React.FC<CurrencyPickerProps> = ({
  value,
  onChange,
  label,
  disabled = false,
  size = 'medium',
  showSymbol = true
}) => {
  const currencyOptions = getCurrencyOptions();
  
  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          padding: '6px 8px',
          fontSize: '13px',
          height: '32px'
        };
      case 'large':
        return {
          padding: '14px 16px',
          fontSize: '16px',
          height: '48px'
        };
      default: // medium
        return {
          padding: '10px 12px',
          fontSize: '14px',
          height: '40px'
        };
    }
  };

  const sizeStyles = getSizeStyles();

  return (
    <div className="currency-picker">
      {label && (
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '14px',
          fontWeight: 'var(--font-weight-medium)',
          color: disabled ? 'var(--color-gray-400)' : 'var(--color-gray-900)'
        }}>
          {label}
        </label>
      )}
      
      <div style={{ position: 'relative' }}>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          style={{
            width: '100%',
            border: '1px solid var(--color-gray-200)',
            borderRadius: 'var(--radius-base)',
            backgroundColor: disabled ? 'var(--color-gray-50)' : 'white',
            color: disabled ? 'var(--color-gray-400)' : 'var(--color-gray-900)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            appearance: 'none',
            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
            backgroundPosition: 'right 8px center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '16px',
            paddingRight: '32px',
            ...sizeStyles
          }}
        >
          {currencyOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {showSymbol ? `${option.symbol} ${option.label}` : option.label}
            </option>
          ))}
        </select>
        
        {/* Show current currency symbol as visual indicator */}
        {showSymbol && value && (
          <div style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: sizeStyles.fontSize,
            color: 'var(--color-gray-600)',
            pointerEvents: 'none',
            fontWeight: 'var(--font-weight-medium)'
          }}>
            {getCurrencySymbol(value)}
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrencyPicker;
